import { MOTION_ENTRANCE, MOTION_EXIT, MOTION_POSITION } from '@/lib/ui/easing';

export const HOT_TAKES_SLIDE_OFFSET_PX = 24 as const;
export const HOT_TAKES_BLUR_ENTER = 'blur(8px)' as const;
export const HOT_TAKES_BLUR_SETTLED = 'blur(0px)' as const;
export const HOT_TAKES_BLUR_EXIT = 'blur(4px)' as const;

/** +100ms over blueprint position/exit durations for a more legible deblur. */
export const HOT_TAKES_SLIDE_TRANSITION = {
  duration: MOTION_POSITION.duration + 0.1,
  ease: MOTION_POSITION.ease,
} as const;

export const HOT_TAKES_SLIDE_EXIT_TRANSITION = {
  duration: MOTION_EXIT.duration + 0.1,
  ease: MOTION_EXIT.ease,
} as const;

/** Snappier slide when the user clicks prev/next, a dot, or uses arrow keys. */
export const HOT_TAKES_SLIDE_MANUAL_TRANSITION = {
  duration: MOTION_ENTRANCE.duration,
  ease: MOTION_ENTRANCE.ease,
} as const;

export const HOT_TAKES_SLIDE_MANUAL_EXIT_TRANSITION = {
  duration: MOTION_EXIT.duration,
  ease: MOTION_EXIT.ease,
} as const;

export type HotTakesSlideDirection = 1 | -1;

/** Auto-advance uses the slower deblur-friendly timing; manual nav is snappier. */
export type HotTakesSlidePace = 'auto' | 'manual';

export const HOT_TAKES_SLIDE_REDUCED_TRANSITION = { duration: 0 } as const;

interface HotTakesSlideMotion {
  initial: false | { opacity: number; x: number; filter: string };
  animate: { opacity: number; x: number; filter: string };
  exit: {
    opacity: number;
    x: number;
    filter: string;
    transition?:
      | typeof HOT_TAKES_SLIDE_EXIT_TRANSITION
      | typeof HOT_TAKES_SLIDE_MANUAL_EXIT_TRANSITION;
  };
  transition:
    | typeof HOT_TAKES_SLIDE_TRANSITION
    | typeof HOT_TAKES_SLIDE_MANUAL_TRANSITION
    | typeof HOT_TAKES_SLIDE_REDUCED_TRANSITION;
}

/** Slide + deblur variants for the Hot Takes carousel — forward enters from the right. */
export function hotTakesSlideMotion(
  direction: HotTakesSlideDirection,
  reduced: boolean,
  pace: HotTakesSlidePace = 'auto',
): HotTakesSlideMotion {
  if (reduced) {
    return {
      initial: false,
      animate: { opacity: 1, x: 0, filter: HOT_TAKES_BLUR_SETTLED },
      exit: { opacity: 1, x: 0, filter: HOT_TAKES_BLUR_SETTLED },
      transition: HOT_TAKES_SLIDE_REDUCED_TRANSITION,
    };
  }

  const offset = direction * HOT_TAKES_SLIDE_OFFSET_PX;
  const enterTransition =
    pace === 'manual' ? HOT_TAKES_SLIDE_MANUAL_TRANSITION : HOT_TAKES_SLIDE_TRANSITION;
  const exitTransition =
    pace === 'manual' ? HOT_TAKES_SLIDE_MANUAL_EXIT_TRANSITION : HOT_TAKES_SLIDE_EXIT_TRANSITION;

  return {
    initial: { opacity: 0, x: offset, filter: HOT_TAKES_BLUR_ENTER },
    animate: { opacity: 1, x: 0, filter: HOT_TAKES_BLUR_SETTLED },
    exit: {
      opacity: 0,
      x: -offset,
      filter: HOT_TAKES_BLUR_EXIT,
      transition: exitTransition,
    },
    transition: enterTransition,
  };
}

export function resolveHotTakesSlideDirection(
  current: number,
  next: number,
): HotTakesSlideDirection {
  if (next >= current) return 1;
  return -1;
}
